/**
 * AuthBootstrap — runs once before the app renders routes.
 *
 * Handles cross-subdomain auth token handoff:
 *
 * 1. If the URL contains ?token=...&orgId=..., save them via auth domain
 *    helpers and strip the params from the URL (clean browser address bar).
 *
 * 2. If no token is present after that, redirect to Pioneer signin
 *    with a ?returnTo= param so Pioneer can send the user back after login.
 *
 * localStorage is per-origin, so pioneer-marketplace.transcent.in cannot
 * read pioneer-staging.transcent.in's storage.  This component bridges
 * that gap via URL parameters.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getToken, setToken, setSelectedOrg, setUser } from '../domains/auth';

const PIONEER_SIGNIN =
  process.env.REACT_APP_PIONEER_URL || 'https://pioneer-staging.transcent.in';

export default function AuthBootstrap({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // ── Step 1: Absorb token from URL (handoff from Pioneer) ──────────
    const urlToken = params.get('token');
    const urlOrgId = params.get('orgId');
    const urlUser = params.get('user');

    if (urlToken) {
      setToken(urlToken);
      if (urlOrgId) setSelectedOrg(urlOrgId);
      if (urlUser) {
        try {
          setUser(decodeURIComponent(urlUser));
        } catch {
          // ignore decode errors
        }
      }

      // Strip auth params from URL (keep other params and the path)
      params.delete('token');
      params.delete('orgId');
      params.delete('user');
      const cleanSearch = params.toString() ? `?${params.toString()}` : '';
      navigate(`${location.pathname}${cleanSearch}`, { replace: true });
      setReady(true);
      return;
    }

    // ── Step 2: If no token, redirect to Pioneer for login ────────────
    const token = getToken();
    if (!token) {
      // Build returnTo URL so Pioneer can redirect back after login
      const returnTo = encodeURIComponent(
        `${window.location.origin}${location.pathname}${location.search}`
      );
      window.location.href = `${PIONEER_SIGNIN}/signin?returnTo=${returnTo}`;
      return;
    }

    setReady(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready) return null;
  return <>{children}</>;
}
